import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react'
import {
  getDisponibilidade,
  createDisponibilidade,
  updateDisponibilidade,
  deleteDisponibilidade,
  copyDisponibilidade,
  getProfissionais,
  getUnidades,
  type DisponibilidadeRegra,
  type Profissional,
  type Unidade,
} from '@/api/adminApi'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import adminStyles from './Admin.module.css'
import styles from './AdminDisponibilidadePage.module.css'

function useMinWidth(px: number) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(min-width: ${px}px)`).matches
  )
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${px}px)`)
    const onChange = () => setMatches(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [px])
  return matches
}

function CollapsibleSection({
  title,
  hint,
  children,
  forceOpen,
  className,
}: {
  title: string
  hint: string
  children: ReactNode
  forceOpen: boolean
  className?: string
}) {
  return (
    <details
      className={`${adminStyles.sectionCard} ${styles.collapsible} ${className ?? ''}`.trim()}
      open={forceOpen || undefined}
    >
      <summary className={styles.collapsibleSummary}>
        <span className={styles.collapsibleTitle}>{title}</span>
        <span className={styles.collapsibleChevron} aria-hidden />
      </summary>
      <div className={styles.collapsibleBody}>
        <p className={styles.cardHint}>{hint}</p>
        {children}
      </div>
    </details>
  )
}

const DIAS = [
  { value: 0, label: 'Domingo', short: 'Dom' },
  { value: 1, label: 'Segunda', short: 'Seg' },
  { value: 2, label: 'Terça', short: 'Ter' },
  { value: 3, label: 'Quarta', short: 'Qua' },
  { value: 4, label: 'Quinta', short: 'Qui' },
  { value: 5, label: 'Sexta', short: 'Sex' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
]

function findDuplicateFaixa(
  list: DisponibilidadeRegra[],
  opts: {
    unidadeId: number
    tipo: 'semanal' | 'especifica'
    dia_semana: number
    data_especifica: string
    excludeId?: number
  }
): boolean {
  const dataEsp = opts.tipo === 'especifica' ? opts.data_especifica.trim() : null
  return list.some((r) => {
    if (opts.excludeId != null && r.id === opts.excludeId) return false
    if (!r.ativo) return false
    if (Number(r.unidade_id) !== opts.unidadeId) return false
    const rowData = r.data_especifica?.trim() || null
    if (dataEsp) return rowData === dataEsp
    return !rowData && Number(r.dia_semana) === opts.dia_semana
  })
}

export function AdminDisponibilidadePage() {
  const [profissionais, setProfissionais] = useState<Profissional[]>([])
  const [unidades, setUnidades] = useState<Unidade[]>([])
  const [profissionalId, setProfissionalId] = useState<number | null>(null)
  const [list, setList] = useState<DisponibilidadeRegra[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<DisponibilidadeRegra | null>(null)
  const [form, setForm] = useState({
    tipo: 'semanal' as 'semanal' | 'especifica',
    unidade_id: 0,
    dia_semana: 1,
    data_especifica: '',
    hora_inicio: '09:00',
    hora_fim: '18:00',
    ativo: true,
  })
  const [saving, setSaving] = useState(false)
  const [copyTargetId, setCopyTargetId] = useState<number | ''>('')
  const [copyReplace, setCopyReplace] = useState(false)
  const [copying, setCopying] = useState(false)
  const [blockData, setBlockData] = useState('')
  const [blockUnidadeId, setBlockUnidadeId] = useState(0)
  const [blocking, setBlocking] = useState(false)
  const { notifySaved, notifyDeleted, notifyError, notifyValidation } = useSaveFeedback()
  const isDesktop = useMinWidth(1024)

  useEffect(() => {
    Promise.all([getProfissionais(true), getUnidades(true)])
      .then(([p, u]) => {
        setProfissionais(p)
        setUnidades(u)
        if (p.length > 0) setProfissionalId((prev) => prev ?? p[0].id)
        if (u.length > 0) {
          setForm((f) => ({ ...f, unidade_id: f.unidade_id > 0 ? f.unidade_id : u[0].id }))
          setBlockUnidadeId(u[0].id)
        }
      })
      .catch(() => {
        setProfissionais([])
        setUnidades([])
      })
  }, [])

  const load = useCallback(() => {
    if (profissionalId == null) {
      setList([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    getDisponibilidade(profissionalId)
      .then(setList)
      .catch((e) => setError(e instanceof Error ? e.message : 'Erro ao carregar'))
      .finally(() => setLoading(false))
  }, [profissionalId])

  useEffect(() => {
    load()
  }, [load])

  const sortedList = useMemo(
    () =>
      [...list].sort((a, b) => {
        const aEsp = a.data_especifica ?? ''
        const bEsp = b.data_especifica ?? ''
        if (aEsp !== bEsp) {
          if (!aEsp) return -1
          if (!bEsp) return 1
          return aEsp.localeCompare(bEsp)
        }
        return (
          a.dia_semana - b.dia_semana ||
          (a.unidade_nome ?? '').localeCompare(b.unidade_nome ?? '') ||
          a.hora_inicio.localeCompare(b.hora_inicio)
        )
      }),
    [list]
  )

  const profissionalNome = profissionais.find((p) => p.id === profissionalId)?.nome

  const resetForm = () => {
    setEditing(null)
    setForm({
      tipo: 'semanal',
      unidade_id: unidades[0]?.id ?? 0,
      dia_semana: 1,
      data_especifica: '',
      hora_inicio: '09:00',
      hora_fim: '18:00',
      ativo: true,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (profissionalId == null) return
    if (!form.unidade_id) {
      const msg = 'Seleccione a unidade onde o profissional estará disponível.'
      setError(msg)
      notifyValidation(msg)
      return
    }
    if (form.hora_fim <= form.hora_inicio) {
      const msg = 'A hora de fim deve ser posterior à hora de início.'
      setError(msg)
      notifyValidation(msg)
      return
    }
    if (form.tipo === 'especifica' && !form.data_especifica.trim()) {
      const msg = 'Indique a data do horário específico.'
      setError(msg)
      notifyValidation(msg)
      return
    }
    if (
      form.ativo &&
      findDuplicateFaixa(list, {
        unidadeId: form.unidade_id,
        tipo: form.tipo,
        dia_semana: form.dia_semana,
        data_especifica: form.data_especifica,
        excludeId: editing?.id,
      })
    ) {
      const msg =
        form.tipo === 'especifica'
          ? 'Já existe um horário activo para esta data nesta unidade. Edite a faixa existente.'
          : 'Já existe um horário semanal activo para este dia nesta unidade. Escolha outra unidade ou edite a faixa existente.'
      setError(msg)
      notifyValidation(msg)
      return
    }
    setSaving(true)
    setError(null)
    const bodyBase = {
      unidade_id: form.unidade_id,
      hora_inicio: form.hora_inicio,
      hora_fim: form.hora_fim,
      ativo: form.ativo,
    }
    const bodyEspecifica =
      form.tipo === 'especifica'
        ? { data_especifica: form.data_especifica.trim() }
        : { dia_semana: form.dia_semana, data_especifica: null as string | null }
    try {
      if (editing) {
        await updateDisponibilidade(editing.id, { ...bodyBase, ...bodyEspecifica })
      } else {
        await createDisponibilidade({
          profissional_id: profissionalId,
          ...bodyBase,
          ...bodyEspecifica,
        })
      }
      resetForm()
      load()
      notifySaved(
        editing
          ? 'Horário actualizado.'
          : form.tipo === 'especifica'
            ? 'Data específica adicionada.'
            : 'Faixa semanal adicionada.'
      )
    } catch (err) {
      notifyError(err, 'Erro ao guardar')
      setError(err instanceof Error ? err.message : 'Erro ao guardar')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (row: DisponibilidadeRegra) => {
    setEditing(row)
    const esp = row.data_especifica ?? ''
    setForm({
      tipo: esp ? 'especifica' : 'semanal',
      unidade_id: row.unidade_id ?? unidades[0]?.id ?? 0,
      dia_semana: row.dia_semana,
      data_especifica: esp,
      hora_inicio: row.hora_inicio,
      hora_fim: row.hora_fim,
      ativo: !!row.ativo,
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Remover esta faixa de horário?')) return
    setError(null)
    try {
      await deleteDisponibilidade(id)
      if (editing?.id === id) resetForm()
      load()
      notifyDeleted('Faixa horária removida.')
    } catch (err) {
      notifyError(err, 'Erro ao excluir')
      setError(err instanceof Error ? err.message : 'Erro ao excluir')
    }
  }

  const diaMeta = (n: number) => DIAS.find((d) => d.value === n)

  const handleCopy = async () => {
    if (profissionalId == null) return
    if (!copyTargetId || copyTargetId === profissionalId) {
      notifyValidation('Seleccione outro profissional como destino.')
      return
    }
    setCopying(true)
    setError(null)
    try {
      const result = await copyDisponibilidade({
        from_profissional_id: profissionalId,
        to_profissional_id: Number(copyTargetId),
        replace: copyReplace,
      })
      notifySaved(`${result.copied} faixa(s) copiada(s).`)
    } catch (err) {
      notifyError(err, 'Erro ao copiar')
      setError(err instanceof Error ? err.message : 'Erro ao copiar')
    } finally {
      setCopying(false)
    }
  }

  const handleBlockDay = async (e: React.FormEvent) => {
    e.preventDefault()
    if (profissionalId == null) return
    if (!blockData.trim()) {
      notifyValidation('Indique a data a bloquear.')
      return
    }
    if (!blockUnidadeId) {
      notifyValidation('Seleccione a unidade.')
      return
    }
    const alreadyBlocked = list.some(
      (r) =>
        !r.ativo &&
        r.data_especifica === blockData.trim() &&
        Number(r.unidade_id) === blockUnidadeId
    )
    if (alreadyBlocked) {
      notifyValidation('Este dia já está bloqueado nesta unidade.')
      return
    }
    setBlocking(true)
    setError(null)
    try {
      await createDisponibilidade({
        profissional_id: profissionalId,
        unidade_id: blockUnidadeId,
        data_especifica: blockData.trim(),
        hora_inicio: '00:00',
        hora_fim: '00:00',
        ativo: false,
      })
      setBlockData('')
      load()
      notifySaved('Dia bloqueado.')
    } catch (err) {
      notifyError(err, 'Erro ao bloquear dia')
      setError(err instanceof Error ? err.message : 'Erro ao bloquear dia')
    } finally {
      setBlocking(false)
    }
  }

  const profissionalCtx =
    profissionalNome != null
      ? `Profissional actual: ${profissionalNome}.`
      : 'Seleccione um profissional abaixo.'

  return (
    <div className={styles.page}>
      <header className={adminStyles.sectionHeader}>
        <h1 className={adminStyles.sectionTitle}>Horários e disponibilidade</h1>
        <p className={adminStyles.sectionSubtitle}>
          Defina horários <strong>semanais</strong> (ex.: todas as terças na Unidade A) ou <strong>datas específicas</strong>{' '}
          (ex.: 07/07/2026 em Londres das 9h às 17h). Nesse dia o profissional só aparece disponível na unidade indicada.
        </p>
      </header>

      {error && (
        <div className={adminStyles.erroSection}>
          <p className={adminStyles.erro}>{error}</p>
        </div>
      )}

      <div className={styles.sections}>
      <div className={`${styles.toolbar} ${styles.orderToolbar}`}>
        <div className={styles.toolbarField}>
          <label htmlFor="disp-profissional">Profissional</label>
          <select
            id="disp-profissional"
            value={profissionalId ?? ''}
            onChange={(e) => {
              setProfissionalId(Number(e.target.value) || null)
              resetForm()
            }}
          >
            {profissionais.length === 0 ? (
              <option value="">Nenhum profissional</option>
            ) : (
              profissionais.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))
            )}
          </select>
        </div>
        <p className={styles.toolbarHint}>
          {profissionalNome
            ? `Horários de ${profissionalNome}. Uma faixa activa por dia/unidade.`
            : 'Adicione profissionais e unidades antes de configurar horários.'}
        </p>
      </div>

      <div className={`${adminStyles.sectionCard} ${styles.orderForm}`}>
        <form onSubmit={handleSubmit}>
          <h2 className={styles.cardTitle}>{editing ? 'Editar faixa horária' : 'Nova faixa horária'}</h2>
          <p className={styles.cardHint}>
            Os clientes só verão estes horários na unidade escolhida.
          </p>

          <div className={styles.tipoRow}>
            <label className={`${styles.tipoOption} ${form.tipo === 'semanal' ? styles.tipoOptionActive : ''}`}>
              <input
                type="radio"
                name="disp-tipo"
                checked={form.tipo === 'semanal'}
                onChange={() => setForm((f) => ({ ...f, tipo: 'semanal' }))}
              />
              <span>Repete todas as semanas</span>
            </label>
            <label className={`${styles.tipoOption} ${form.tipo === 'especifica' ? styles.tipoOptionActive : ''}`}>
              <input
                type="radio"
                name="disp-tipo"
                checked={form.tipo === 'especifica'}
                onChange={() => setForm((f) => ({ ...f, tipo: 'especifica' }))}
              />
              <span>Data específica</span>
            </label>
          </div>

          <div className={styles.formGrid}>
            <div className={adminStyles.formGroup}>
              <label htmlFor="disp-unidade">Unidade</label>
              <select
                id="disp-unidade"
                value={form.unidade_id || ''}
                onChange={(e) => setForm((f) => ({ ...f, unidade_id: Number(e.target.value) }))}
                required
                disabled={unidades.length === 0}
              >
                {unidades.length === 0 ? (
                  <option value="">Sem unidades</option>
                ) : (
                  unidades.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))
                )}
              </select>
            </div>
            {form.tipo === 'semanal' ? (
              <div className={adminStyles.formGroup}>
                <label htmlFor="disp-dia">Dia da semana</label>
                <select
                  id="disp-dia"
                  value={form.dia_semana}
                  onChange={(e) => setForm((f) => ({ ...f, dia_semana: Number(e.target.value) }))}
                >
                  {DIAS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className={adminStyles.formGroup}>
                <label htmlFor="disp-data">Data</label>
                <input
                  id="disp-data"
                  type="date"
                  value={form.data_especifica}
                  onChange={(e) => setForm((f) => ({ ...f, data_especifica: e.target.value }))}
                  required
                />
              </div>
            )}
            <div className={`${adminStyles.formGroup} ${styles.timeField}`}>
              <label htmlFor="disp-inicio">Início</label>
              <input
                id="disp-inicio"
                type="time"
                value={form.hora_inicio}
                onChange={(e) => setForm((f) => ({ ...f, hora_inicio: e.target.value.slice(0, 5) }))}
                required
              />
            </div>
            <div className={`${adminStyles.formGroup} ${styles.timeField}`}>
              <label htmlFor="disp-fim">Fim</label>
              <input
                id="disp-fim"
                type="time"
                value={form.hora_fim}
                onChange={(e) => setForm((f) => ({ ...f, hora_fim: e.target.value.slice(0, 5) }))}
                required
              />
            </div>
          </div>

          <div className={styles.checkboxInline}>
            <input
              id="disp-ativo"
              type="checkbox"
              checked={form.ativo}
              onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
            />
            <label htmlFor="disp-ativo">Faixa activa (visível no agendamento)</label>
          </div>

          <div className={`${adminStyles.formActions} ${styles.formActionsSticky}`}>
            <button
              type="submit"
              className={adminStyles.btnSalvar}
              disabled={saving || profissionalId == null || unidades.length === 0}
            >
              {saving ? 'A guardar…' : editing ? 'Guardar alterações' : 'Adicionar faixa'}
            </button>
            {editing && (
              <button type="button" className={adminStyles.btnCancel} onClick={resetForm}>
                Cancelar edição
              </button>
            )}
          </div>
        </form>
      </div>

      <div className={`${adminStyles.sectionListCard} ${styles.orderList}`}>
        <h2 className={adminStyles.sectionListTitle}>
          Horários configurados
          {profissionalNome ? (
            <span className={styles.listCount}> ({sortedList.length})</span>
          ) : null}
        </h2>
        {loading ? (
          <p className={adminStyles.placeholderMsg}>A carregar horários…</p>
        ) : sortedList.length === 0 ? (
          <p className={adminStyles.listEmpty}>
            Nenhuma regra. O app usa horário padrão 08:00–18:00 até adicionar faixas.
          </p>
        ) : (
          <div className={styles.listScrollArea}>
            <ul className={`${adminStyles.list} ${styles.dispList}`}>
              {sortedList.map((row) => {
                const dia = diaMeta(row.dia_semana)
                const isEditing = editing?.id === row.id
                const dataEsp = row.data_especifica
                const isBlock = !!dataEsp && !row.ativo
                const dataLabel = dataEsp ? dataEsp.split('-').reverse().join('/') : null
                return (
                  <li
                    key={row.id}
                    className={`${adminStyles.listItem} ${styles.dispListItem} ${isEditing ? styles.listItemActive : ''}`}
                  >
                    <div className={adminStyles.listItemContent}>
                      <div className={`${adminStyles.listItemTitle} ${styles.listItemTitleRow}`}>
                        {dataLabel ? (
                          <span className={styles.dateBadge}>{dataLabel}</span>
                        ) : (
                          <span className={styles.dayBadge}>{dia?.short ?? '?'}</span>
                        )}
                        <span className={styles.unitBadge}>{row.unidade_nome ?? 'Unidade'}</span>
                      </div>
                      <div className={styles.dispListMeta}>
                        {!isBlock && (
                          <span className={styles.timeRange}>
                            {row.hora_inicio} – {row.hora_fim}
                          </span>
                        )}
                        {dataLabel && !isBlock && <span className={styles.specificTag}>Específica</span>}
                        {isBlock && <span className={styles.blockedTag}>Bloqueado</span>}
                        {!row.ativo && !isBlock && <span className={styles.inactiveTag}>Inactiva</span>}
                      </div>
                    </div>
                    <div className={`${adminStyles.listItemActions} ${styles.dispListActions}`}>
                      <button
                        type="button"
                        className={adminStyles.btnSecondary}
                        onClick={() => handleEdit(row)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className={adminStyles.btnDanger}
                        onClick={() => handleDelete(row.id)}
                      >
                        Excluir
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>

      <CollapsibleSection
        title="Copiar horários"
        hint={`${profissionalCtx} Copie as faixas deste profissional para outro.`}
        forceOpen={isDesktop}
        className={styles.orderCopy}
      >
        <div className={styles.copyGrid}>
          <div className={`${adminStyles.formGroup} ${styles.gridField}`}>
            <label htmlFor="copy-target">Profissional destino</label>
            <select
              id="copy-target"
              value={copyTargetId}
              onChange={(e) => setCopyTargetId(Number(e.target.value) || '')}
              disabled={profissionalId == null}
            >
              <option value="">— Seleccionar —</option>
              {profissionais
                .filter((p) => p.id !== profissionalId)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
            </select>
          </div>
          <div className={styles.copyOptions}>
            <div className={styles.checkboxInline}>
              <input
                id="copy-replace"
                type="checkbox"
                checked={copyReplace}
                onChange={(e) => setCopyReplace(e.target.checked)}
              />
              <label htmlFor="copy-replace">Substituir horários no destino</label>
            </div>
            <button
              type="button"
              className={adminStyles.btnSecondary}
              onClick={handleCopy}
              disabled={copying || profissionalId == null}
            >
              {copying ? 'A copiar…' : 'Copiar horários'}
            </button>
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Bloquear dia"
        hint={`${profissionalCtx} Marca um dia como indisponível na unidade escolhida.`}
        forceOpen={isDesktop}
        className={styles.orderBlock}
      >
        <form onSubmit={handleBlockDay}>
          <div className={styles.formGridTwo}>
            <div className={`${adminStyles.formGroup} ${styles.gridField}`}>
              <label htmlFor="block-data">Data</label>
              <input
                id="block-data"
                type="date"
                value={blockData}
                onChange={(e) => setBlockData(e.target.value)}
                required
              />
            </div>
            <div className={`${adminStyles.formGroup} ${styles.gridField}`}>
              <label htmlFor="block-unidade">Unidade</label>
              <select
                id="block-unidade"
                value={blockUnidadeId || ''}
                onChange={(e) => setBlockUnidadeId(Number(e.target.value))}
                disabled={unidades.length === 0}
              >
                {unidades.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={adminStyles.formActions}>
            <button
              type="submit"
              className={adminStyles.btnDanger}
              disabled={blocking || profissionalId == null || unidades.length === 0}
            >
              {blocking ? 'A bloquear…' : 'Bloquear dia'}
            </button>
          </div>
        </form>
      </CollapsibleSection>
      </div>
    </div>
  )
}
